import { Link } from "react-router-dom";
import { motion } from "framer-motion";

// Split panel hero — solid brand-dark content pane on the left, photo on the
// right, separated by an angled seam (nods to the logo's roofline chevron).
// Deliberately not a full-bleed photo + dark-scrim + centered text layout,
// which is the generic pattern most PEB/steel-fab template sites reach for.
const HeroSection = ({
  title,
  subtitle,
  description,
  backgroundImage,
  primaryButtonText,
  primaryButtonLink,
  secondaryButtonText,
  secondaryButtonLink,
}) => {
  return (
    <section className="relative overflow-hidden bg-foreground">
      <div className="relative grid md:grid-cols-[1.15fr_0.85fr] min-h-[48vh] sm:min-h-[54vh]">
        {/* Content pane */}
        <div className="relative z-10 flex items-center bg-foreground px-4 md:px-0">
          <div
            className="hidden md:block absolute inset-y-0 -right-16 w-32 bg-foreground z-10"
            style={{ clipPath: "polygon(0 0, 45% 0, 100% 100%, 55% 100%)" }}
            aria-hidden="true"
          />
          <div className="relative z-20 container-custom md:mx-0 md:pl-8 lg:pl-12 md:pr-20 py-16 md:py-0 max-w-2xl">
            {subtitle && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-5"
              >
                <span className="inline-block bg-primary/15 border border-primary/40 text-primary font-montserrat font-semibold text-sm tracking-wide px-4 py-1.5 rounded-full">
                  {subtitle}
                </span>
                <span className="block w-14 h-[3px] bg-secondary rounded-full mt-3" aria-hidden="true" />
              </motion.div>
            )}

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-montserrat text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-5 leading-tight"
            >
              {title}
            </motion.h1>

            {description && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-white/75 text-base sm:text-lg mb-8"
              >
                {description}
              </motion.p>
            )}

            {(primaryButtonText || secondaryButtonText) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                {primaryButtonText && primaryButtonLink && (
                  <Link to={primaryButtonLink} className="btn-primary text-center">
                    {primaryButtonText}
                  </Link>
                )}
                {secondaryButtonText && secondaryButtonLink && (
                  <Link
                    to={secondaryButtonLink}
                    className="inline-flex items-center justify-center border-2 border-white/70 text-white px-6 py-3 rounded-lg font-montserrat font-semibold hover:bg-white hover:text-foreground transition"
                  >
                    {secondaryButtonText}
                  </Link>
                )}
              </motion.div>
            )}
          </div>
        </div>

        {/* Image pane */}
        <div className="relative min-h-[220px] md:min-h-0">
          <img
            src={backgroundImage}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 md:from-transparent to-transparent" aria-hidden="true" />
        </div>
      </div>

      {/* Signature brand line closing the section */}
      <div className="h-[3px] w-full bg-gradient-to-r from-primary to-secondary" aria-hidden="true" />
    </section>
  );
};

export default HeroSection;
